import { ReactNode } from 'react';
import { Drawer, Modal, Spin } from '@arco-design/web-react';
import usePopupTopLayer from '../hooks/usePopupTopLayer';

/**
 * 抽屉宽度档位（按**职能 + 规模**取档；同一职能在页面之间必须同档，随手写数值即漂移）。
 * 职能 → 档：
 * - `compact` 420：单列窄面板（界面设置、流程节点配置、IVR 面板）；
 * - `narrow`  520：只读明细单块（拦截历史、统计小弹层）、意图测试台；
 * - `medium`  680：表单/编辑器，以及**通话详情**（与共享件 `CallDetailDrawer` 同档）；
 * - `wide`    720：明细含表格或多段内容（调用详情、指标下钻、质检明细、排队明细）、**会话详情**；
 * - `list`    900：列表/证据/多列明细（证据列表、字段配置、知识数据面板）；
 * - `studio` 1000：双栏/联调/工作台宽面板（运行记录、LLM 联调台）；
 * - `result` 1160：结果核对宽表——列多（含动态结果字段）且要求**逐行直读、一屏读全不横滚**（外呼结果明细）。
 *   与 `list` 的分工：`list` 放得下就横滚、辅助列去承担滚动；`result` 是「结果内容不许被挤出可视区」的场景，
 *   故列数必须先收敛到核对面真正要看的字段，再按列宽之和取本档，不能靠本档兜住无节制的加列；
 * - `source` 1160：**原文查看**——工作台「智能辅助」卡里点引用后的「查看原文」，内容是整份文档
 *   （口径表格 / PDF / markdown 网页版）。判据与 `result` 同族（内容不许被挤出可视区），但用途不同：
 *   那里是核对表、这里是原文正文；宽度取同一值是因为两侧要读的东西一样宽，不必再造一个新数字。
 * - `canvas` 1180：流程图设计器画布（唯一最宽档，不得他用）。
 * 规模判断：字段 >20 或明细内含表格/时间线的只读详情取 `wide` 起；字段 ≤8 且无附表的只读明细取
 * `narrow`/`medium`——避免「6 个字段占 720、24 个字段挤 680」。
 * 档位之外确需专用宽度时，新档必须先进本表（禁止在页面里裸写字面量，门禁 `scripts/check-popup-shell.mjs` R3）。
 * 档位值是**宽度上限**：窗口装不下时改传 `hooks/useDrawerWidth` 的返回值收进窗口（仍以档位取值），
 * 不要在页面里自己算 `window.innerWidth`——两处算法必然漂移。
 */
export const DRAWER_WIDTH = {
  compact: 420,
  narrow: 520,
  medium: 680,
  wide: 720,
  list: 900,
  studio: 1000,
  result: 1160,
  source: 1160,
  canvas: 1180,
} as const;

export interface DetailDrawerProps {
  title?: ReactNode;
  /** 标题右侧辅助信息（编号/来源/时间等短信息），次级色，与标题同一行 */
  subTitle?: ReactNode;
  /**
   * 摘要区：固定在正文顶部，放「一眼要看的几个值」（客户/方向/时长/结果/关联对象），
   * 与下面的字段明细分开——明细可以很长，结论不该被埋在里面；加载中不渲染。
   */
  summary?: ReactNode;
  visible: boolean;
  onCancel: () => void;
  /** 默认 520；取值优先用 `DRAWER_WIDTH` 档位；专用全屏页可传字符串（如 `100vw`） */
  width?: number | string;
  /** 传给外层 Arco Drawer 的类名（专用全屏抽屉等需要解除全局 max-width 时用） */
  drawerClassName?: string;
  /** 滑入方向（默认 right；嵌套抽屉场景传 left 实现左右分屏互不遮挡） */
  placement?: 'right' | 'left' | 'top' | 'bottom';
  /** 底部操作区插槽（约定：1 个主操作 + `MoreActions` 收次要动作；正文内不再重复放主行动按钮） */
  footer?: ReactNode;
  /**
   * 内容加载态：**首屏**（尚未拿到内容、children 还为空）渲染明细骨架屏替代整块转圈；
   * 已拿到内容的二次刷新仍走半透明遮罩（Spin），避免把已读内容换成骨架造成跳动。
   */
  loading?: boolean;
  /**
   * 焦点陷阱开关（默认 true）。详情抽屉上方若还有 Modal 弹层（如申诉/复核），
   * 抽屉的焦点锁会把焦点从 Modal 抢回抽屉按钮，导致弹层输入框无法编辑——此类页面传 false。
   */
  focusLock?: boolean;
  /** 关闭后卸载抽屉 DOM（避免遮罩/面板残留） */
  unmountOnExit?: boolean;
  /**
   * 抽屉内表单有未保存修改：关闭入口（遮罩 / Esc / 右上角 × / 取消）先弹确认。
   * 页面把"是否偏离基线"的判据算好传进来（抽屉壳不猜业务字段）——
   * 抽屉表单此前完全没有离场保护，误点遮罩就把编辑内容丢了。
   */
  dirty?: boolean;
  /** 未保存确认文案（默认通用句） */
  dirtyText?: ReactNode;
  children?: ReactNode;
}

/** 明细骨架：标签 + 值两列若干行，形状与 `Descriptions` 正文对齐（复用骨架屏 shimmer 基元） */
function DetailSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="bcc-skeleton-detail" role="status" aria-label="加载中">
      {Array.from({ length: rows }).map((_, r) => (
        <div className="bcc-skeleton-detail-row" key={r} style={{ animationDelay: `${r * 60}ms` }}>
          <span className="bcc-skeleton-line" style={{ width: `${76 + ((r * 13) % 24)}px` }} />
          <span className="bcc-skeleton-line" style={{ width: `${52 + ((r * 29) % 34)}%` }} />
        </div>
      ))}
    </div>
  );
}

export default function DetailDrawer({
  title,
  subTitle,
  summary,
  visible,
  onCancel,
  width = 520,
  drawerClassName,
  placement = 'right',
  footer,
  loading,
  focusLock = true,
  unmountOnExit,
  dirty,
  dirtyText,
  children,
}: DetailDrawerProps) {
  // 未保存保护：走同一个 onCancel 出口（遮罩 / Esc / × / footer 里的取消都经它）
  const handleCancel = () => {
    if (!dirty) {
      onCancel();
      return;
    }
    Modal.confirm({
      title: '放弃未保存的修改？',
      content: dirtyText ?? '关闭后本次填写的内容不会保留。',
      okText: '放弃修改',
      cancelText: '继续编辑',
      okButtonProps: { status: 'warning' },
      onOk: () => onCancel(),
    });
  };
  // 标题 + 副标题同行；无副标题时保持原样，不改动已有的纯标题渲染
  const resolvedTitle =
    subTitle != null ? (
      <span className="bcc-drawer-title">
        {title}
        <span className="bcc-drawer-title-sub">{subTitle}</span>
      </span>
    ) : (
      title
    );
  // 首屏（无内容）用骨架，二次刷新（已有内容）保留遮罩
  const firstLoad = !!loading && !children;
  // 打开时把本抽屉容器排到 body 末尾（见 hook 注释：从抽屉里再开的详情要被后插入的抽屉盖住时靠它兜底）
  const attachTopLayer = usePopupTopLayer(visible);
  return (
    <Drawer
      title={resolvedTitle}
      visible={visible}
      onCancel={handleCancel}
      width={width}
      className={drawerClassName}
      placement={placement}
      footer={footer ?? null}
      focusLock={focusLock}
      unmountOnExit={unmountOnExit}
    >
      {/* display:contents 中转节点：自身不生成盒子，只用来定位本抽屉的 Arco 容器 */}
      <div ref={attachTopLayer} style={{ display: 'contents' }}>
        {firstLoad ? (
          <DetailSkeleton />
        ) : (
          <Spin loading={!!loading} style={{ display: 'block' }}>
            {summary != null && <div className="bcc-drawer-summary">{summary}</div>}
            {children}
          </Spin>
        )}
      </div>
    </Drawer>
  );
}



